// 📐 #6394 장표 제목 줄 균형 — 마지막 줄에 한 어절(«X»·«체결»)만 남으면 실제 글꼴로 재서 줄을 고르게 다시 나눈다.
//
// ★왜 CSS `text-wrap: balance` 가 아닌가★ 받기 엔진(@remotion/web-renderer)은 글 노드를 조각씩 늘려 높이로 줄 끝을 다시 찾는다
//   (`slide-sheet-check/engine-lines.ts`) — balance 를 따르지 않는다. 그래서 줄 끝을 «\n» 으로 직접 박는다(KO_TEXT 는 pre-line).
//   미리보기·받기·4초 시트 검사가 같은 줄을 본다.
// ★왜 폭 모델(`briefLineCount`)이 아니라 실측인가★ 10-10 실측: 이 맥의 장표 글꼴(Apple SD Gothic Neo 700)은 한글이 0.865em 인데
//   모델은 1.0em 이다 — 모델로는 «안전모 착용 O · 안전대 고리 체결 / X»(장면 3)를 «…안전대 / 고리 체결 X»로 잘못 본다.
//   글꼴은 기계마다 다르므로 그 화면의 글꼴로 잰다(받기는 같은 브라우저에서 굽는다).
// ★바꾸지 않는 것★ 글자(띄어쓰기 한 칸을 «\n» 으로 바꿀 뿐) · 줄 수(늘리지도 줄이지도 않는다 → 높이·자리 계산 그대로) ·
//   이미 고른 제목(마지막 줄이 두 어절 이상이고 가장 긴 줄의 1/3 이상) · 손으로 넣은 줄바꿈 · 붙임 문자(U+00A0·U+2060) · 강조 낱말 안.
//   재지 못하는 곳(jsdom·서버 정적 HTML)은 원문 그대로 = 종전 바이트.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** 마지막 줄이 가장 긴 줄의 이 비율보다 짧으면 «고아»로 본다(한 어절이면 비율과 무관하게 고아). */
export const TITLE_ORPHAN_RATIO = 1 / 3;
/** 다시 나눈 줄의 가장 긴 줄이 쓸 수 있는 폭 비율 — 강조 상자 안쪽 여백·글자 경계 반올림 여유. 넘으면 손대지 않는다. */
export const TITLE_BALANCE_SLACK = 0.98;

/** 끊을 수 있는 자리(일반 띄어쓰기)로 나눈 어절과 그 사이 띄어쓰기 원문. 강조 낱말 안 띄어쓰기는 끊지 않는다. */
function breakWords(text: string, keyword?: string): { words: string[]; gaps: string[] } {
  const keep: Array<[number, number]> = [];
  const kw = typeof keyword === 'string' ? keyword : '';
  if (kw.trim() && /[ \t]/.test(kw)) for (let i = text.indexOf(kw); i >= 0; i = text.indexOf(kw, i + kw.length)) keep.push([i, i + kw.length]);
  const words: string[] = []; const gaps: string[] = [];
  let cur = ''; let pos = 0;
  for (const tok of text.split(/([ \t]+)/)) {
    const isGap = /^[ \t]+$/.test(tok);
    if (isGap && cur && !keep.some(([a, b]) => pos > a && pos < b)) { words.push(cur); gaps.push(tok); cur = ''; }
    else cur += tok;
    pos += tok.length;
  }
  if (cur) words.push(cur);
  else if (gaps.length) gaps.pop(); // 끝 띄어쓰기
  return { words, gaps };
}

/** 어절 폭으로 앞에서부터 채운 줄(어절 번호 목록). */
function greedy(widths: number[], gap: number, avail: number): number[][] {
  const lines: number[][] = [];
  let cur: number[] = []; let w = 0;
  widths.forEach((x, i) => {
    const add = cur.length ? w + gap + x : x;
    if (cur.length && add > avail) { lines.push(cur); cur = [i]; w = x; } else { cur.push(i); w = add; }
  });
  if (cur.length) lines.push(cur);
  return lines;
}

/**
 * 줄 균형 — `measure`(px) 로 잰 어절을 `avail`(px) 폭에 앞에서부터 채웠을 때 마지막 줄이 고아면, 같은 줄 수로 가장 좁게
 *   나눈 줄을 «\n» 으로 이어 돌려준다. 해당 없으면 `text` 그대로(같은 문자열).
 */
export function balanceTitleBreaks(text: string, measure: (s: string) => number, avail: number, keyword?: string): string {
  if (!text || !text.trim() || /\n/.test(text) || !(avail > 0)) return text;
  const lead = /^[ \t]*/.exec(text)?.[0] ?? '';
  const body = text.slice(lead.length);
  const { words, gaps } = breakWords(body, keyword);
  if (words.length < 2) return text;
  const widths = words.map((w) => measure(w));
  const gap = measure(' ');
  if (widths.some((x) => !(x >= 0)) || !(gap >= 0) || Math.max(...widths) > avail) return text;
  const lineW = (l: number[]) => l.reduce((s, i, j) => s + widths[i] + (j ? gap : 0), 0);
  const lines = greedy(widths, gap, avail);
  const n = lines.length;
  if (n < 2) return text;
  const orphan = (ls: number[][]) => ls[ls.length - 1].length === 1 || lineW(ls[ls.length - 1]) < TITLE_ORPHAN_RATIO * Math.max(...ls.map(lineW));
  if (!orphan(lines)) return text;
  // 같은 줄 수가 되는 가장 좁은 폭(이분 탐색) — 그 폭으로 채우면 줄 길이가 고르다.
  let lo = Math.max(...widths); let hi = avail;
  for (let it = 0; it < 30 && hi - lo > 0.25; it++) {
    const mid = (lo + hi) / 2;
    if (greedy(widths, gap, mid).length <= n) hi = mid; else lo = mid;
  }
  const out = greedy(widths, gap, hi);
  if (out.length !== n || Math.max(...out.map(lineW)) > avail * TITLE_BALANCE_SLACK) return text;
  if (out.every((l, i) => l[0] === lines[i][0])) return text; // 끊는 자리가 같다 = 고를 수 없다
  const breakAt = new Set(out.slice(1).map((l) => l[0]));
  let s = lead;
  words.forEach((w, i) => { s += (i === 0 ? '' : breakAt.has(i) ? '\n' : gaps[i - 1]) + w; });
  return s + body.slice(body.trimEnd().length);
}

/* ── 화면에서 재기 ─────────────────────────────────────────────── */

/** 배치가 없는 곳(서버 렌더 · jsdom 시험)은 재지 않는다 — useLayoutEffect 경고를 남기지 않는다(`Overlay169SlideLayer` 관용구). */
const useMeasureEffect = typeof window === 'undefined' || /jsdom/i.test(typeof navigator === 'undefined' ? '' : navigator.userAgent) ? useEffect : useLayoutEffect;

let ctx2d: CanvasRenderingContext2D | null | undefined;
const canvasCtx = (): CanvasRenderingContext2D | null => {
  if (ctx2d !== undefined) return ctx2d;
  try { ctx2d = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d'); } catch { ctx2d = null; }
  return ctx2d;
};
const padX = (cs: CSSStyleDeclaration) => (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);

/** 요소의 글꼴로 재서 고른 제목. `avail` = 'self'(요소 자기 폭 · 폭이 정해진 띠) | 'parent'(감싼 칸 안쪽 폭 · 글 길이만큼 줄어드는 제목). */
export function balanceInElement(el: HTMLElement, text: string, avail: 'self' | 'parent', keyword?: string): string {
  const ctx = canvasCtx();
  const box = avail === 'self' ? el : el.parentElement;
  if (!ctx || !box) return text;
  const cs = getComputedStyle(el);
  const width = box.clientWidth - padX(getComputedStyle(box));
  if (!(width > 0)) return text;
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
  const ls = parseFloat(cs.letterSpacing) || 0;
  return balanceTitleBreaks(text, (s) => ctx.measureText(s).width + ls * [...s].length, width, keyword);
}

/**
 * 제목 요소에 붙이는 훅 — `[ref, 그릴 글]`. 그린 뒤 재서 고아면 같은 커밋 안에서 다시 그린다(화면에 칠하기 전 · 받기 프레임도 같다).
 *   매 렌더 다시 잰다(글꼴이 늦게 와도 따라간다) · 결과가 같으면 상태를 안 바꾼다.
 */
export function useBalancedTitle<E extends HTMLElement = HTMLDivElement>(text: string | undefined, opts: { avail: 'self' | 'parent'; keyword?: string }) {
  const ref = useRef<E>(null);
  const src = text ?? '';
  const [shown, setShown] = useState<{ src: string; out: string } | null>(null);
  const { avail, keyword } = opts;
  useMeasureEffect(() => {
    const el = ref.current;
    const out = el ? balanceInElement(el, src, avail, keyword) : src;
    setShown((prev) => (out === src ? (prev === null ? prev : null) : prev && prev.src === src && prev.out === out ? prev : { src, out }));
  });
  // ref 타입은 useRef 가 돌려준 그대로 — @types/react 18(RefObject<E>)·19(RefObject<E | null>) 둘 다 `ref={…}` 에 맞는다.
  const result: [typeof ref, string] = [ref, shown && shown.src === src ? shown.out : src];
  return result;
}
